import { useMemo, MouseEventHandler, MouseEvent } from "react"
import { useTranslation } from "react-i18next"
import { Button, Icon, Link } from "@oysterhr/ui"
import { useReusableWizard } from "@oysterhr/feature-components"

interface NavigationButtonProps {
  loading: boolean
  disabled?: boolean
  onNext?: MouseEventHandler<HTMLButtonElement>
  isFirst?: boolean
  isLast?: boolean
  buttonText?: string
  analyticsBase: string
}

export const NavigationButtons = ({
  loading,
  disabled,
  onNext,
  isFirst = false,
  isLast = false,
  buttonText,
  analyticsBase,
}: NavigationButtonProps) => {
  const { goToPrevious, isNextDisabled } = useReusableWizard()
  const { t } = useTranslation("company_interface/CompanyNewHireFTEPage")
  const defaultContinueText = t(isLast ? "confirm" : "continue", { ns: "Wizard" })

  /**
   * For analytics purposes, we need to trigger actions when the user clicks the next button
   * Since our next button has two functionalities right now (moving forward on the wizard, or showing the confirmation modal)
   * we need to know which one is being triggered
   */
  const analyticsName = useMemo(() => {
    if (isLast) return `${analyticsBase}_generate-report`

    return `${analyticsBase}_continue-button`
  }, [isLast, analyticsBase])

  return (
    <div className="mt-8 flex items-center">
      {isFirst || (
        <Link
          as="button"
          className="mr-6"
          data-analytics-name="salary-analyzer_back-button"
          data-testid="form-back-button"
          onClick={goToPrevious}
          startAdornment={<Icon name="ChevronLeft" />}
          type="button"
        >
          {t("back", { ns: "Wizard" })}
        </Link>
      )}
      <Button
        data-analytics-name={analyticsName}
        type="submit"
        disabled={loading || disabled || isNextDisabled}
        isLoading={loading}
        onClick={onNext}
        onMouseDown={(e: MouseEvent<HTMLButtonElement>) => e.preventDefault()}
      >
        {buttonText ? buttonText : defaultContinueText}
      </Button>
    </div>
  )
}
